import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { E32 } from "../theme/endesga32";

// Square portrait frame for NPC and shopkeeper panels. Tinted fill,
// 3px ink border, 4×4 ink offset shadow, and four 6×6 ink corner
// pixels stamped on the frame's corners — matches the design system's
// `NpcPortrait`. Real art (when authored) would slot in via the
// `children` slot.

interface Props {
  /** Background tint. Defaults to a faction-derived color when
   *  `faction` is provided. Provide `tint` directly to override. */
  tint?: string;
  faction?: string | null;
  /** Square edge length in px. The portrait is always square. */
  size?: number;
  /** Optional npc / shop / location id. When supplied we try to
   *  render `/portraits/npcs/<id>[_<MOOD>].png` filling the frame.
   *  When `mood` is omitted we also fall back to
   *  `/portraits/locations/<id>.png` so shop tiles (which pass a
   *  location-style id like `fruit_stand`) still render. If every
   *  candidate 404s, the tinted background + children fall back into
   *  view. For NPC portraits inside dialogue, pass `mood` so the
   *  right mood-suffixed file loads (e.g. `pip_ra_wuggo_WARY.png`). */
  npcId?: string | null;
  /** Optional mood suffix, e.g. "WARY" or "OFFICIOUS". Mixed case
   *  is normalized to upper. When set, the resolved URL is
   *  `/portraits/npcs/<npcId>_<MOOD>.png`. */
  mood?: string | null;
  /** Contents painted on top of the tinted background (mood badge,
   *  diagonal hatching, future pixel art, etc.). */
  children?: ReactNode;
}

const FACTION_TINTS: Record<string, string> = {
  humans: E32.tan,
  goblins: E32.fern,
  dwarves: E32.brick,
  orcs: E32.forest,
  elves: E32.mulberry,
};

const FALLBACK_TINT = E32.plum;

export function NpcPortrait({
  tint,
  faction,
  size = 180,
  npcId,
  mood,
  children,
}: Props) {
  const fill = tint ?? (faction ? FACTION_TINTS[faction] : null) ?? FALLBACK_TINT;
  // When `mood` is set the caller is unambiguously asking for an NPC
  // portrait — try the mood-suffixed file first, then fall back to
  // the base `<id>.png` so a speaker with no mood variants still
  // renders. Without a mood the same component also renders shop /
  // location tiles, so we extend the chain to `locations/<id>.png`.
  const candidates: string[] = npcId
    ? mood
      ? [
          `/portraits/npcs/${npcId}_${mood.toUpperCase()}.png`,
          `/portraits/npcs/${npcId}.png`,
        ]
      : [`/portraits/npcs/${npcId}.png`, `/portraits/locations/${npcId}.png`]
    : [];
  const [attempt, setAttempt] = useState(0);
  // Reset to the first candidate when the inputs change — without
  // this a stale "failed" from a prior mood would suppress the next
  // (potentially valid) image.
  useEffect(() => {
    setAttempt(0);
  }, [npcId, mood]);
  const src = candidates[attempt] ?? null;
  const showImage = !!src;

  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        background: fill,
        border: `3px solid ${E32.ink}`,
        boxShadow: `4px 4px 0 0 ${E32.ink}`,
        overflow: "hidden",
      }}
    >
      {showImage && (
        <img
          src={src!}
          alt=""
          draggable={false}
          onError={() => setAttempt((n) => n + 1)}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            imageRendering: "pixelated",
          }}
        />
      )}
      {children}
      {/* corner pixels — 6×6 ink squares at each corner */}
      {(["tl", "tr", "bl", "br"] as const).map((c) => {
        const pos: CSSProperties = CORNER_POS[c];
        return (
          <div
            key={c}
            aria-hidden="true"
            style={{
              position: "absolute",
              width: 6,
              height: 6,
              background: E32.ink,
              ...pos,
            }}
          />
        );
      })}
    </div>
  );
}

const CORNER_POS: Record<"tl" | "tr" | "bl" | "br", CSSProperties> = {
  tl: { top: 0, left: 0 },
  tr: { top: 0, right: 0 },
  bl: { bottom: 0, left: 0 },
  br: { bottom: 0, right: 0 },
};

// Helper for the diagonal-hatch overlay used inside several portraits.
// Lives here so callers don't repeat the gradient string.
export function PortraitHatching({ dark = false }: { dark?: boolean }) {
  const stripes = dark
    ? "repeating-linear-gradient(45deg, rgba(255,255,255,0.06) 0 4px, transparent 4px 10px), " +
      "repeating-linear-gradient(-45deg, rgba(0,0,0,0.4) 0 6px, transparent 6px 14px)"
    : "repeating-linear-gradient(45deg, rgba(0,0,0,0.06) 0 4px, transparent 4px 10px)";

  return (
    <div
      aria-hidden="true"
      style={{ position: "absolute", inset: 0, backgroundImage: stripes }}
    />
  );
}

